// @flow

import React from 'react';
import {makeStyles} from '@material-ui/core';
import YouTubeEmbed from '../../../../embedded-content/YouTube/YouTubeEmbed';
import {extractYouTubeVideoId} from '../../../../embedded-content/utils';
import type {CommonEmbedProps} from '../../../../embedded-content/types';
import {PLATFORMS, convertToEmbedUrl, isFacebookVertical} from './videoUtils';
import type {SocialMediaType} from './videoUtils';

const useStyles = makeStyles(() => ({
  embedContainer: {
    width: '100%',
    maxWidth: '100%',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
  },
  youtubeWrapper: {
    position: 'relative',
    paddingBottom: '56.25%', // 16:9 aspect ratio
    height: 0,
    overflow: 'hidden',
    '& iframe': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none',
    },
  },
  verticalVideoWrapper: {
    position: 'relative',
    paddingBottom: '177.78%', // 9:16 aspect ratio
    height: 0,
    overflow: 'hidden',
    maxWidth: '400px',
    margin: '0 auto',
    '& iframe': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      border: 'none',
    },
  },
  facebookWrapper: {
    position: 'relative',
    width: '100%',
    minHeight: '500px',
    '& iframe': {
      width: '100%',
      minHeight: '500px',
      border: 'none',
    },
  },
}));

type ProductVideoEmbedProps = CommonEmbedProps & {
  socialMediaType: SocialMediaType,
  url: string,
};

export default function ProductVideoEmbed(props: ProductVideoEmbedProps) {
  const {socialMediaType, url, ...embedProps} = props;
  const classes = useStyles();

  const platform = socialMediaType;
  const finalEmbedUrl = convertToEmbedUrl(url, platform) || url;

  if (platform === PLATFORMS.YOUTUBE) {
    const videoId = extractYouTubeVideoId(finalEmbedUrl);
    if (!videoId) {
      return <div>Error: No se pudo extraer el ID del video de YouTube</div>;
    }

    return (
      <div className={classes.embedContainer}>
        <div className={classes.youtubeWrapper}>
          <YouTubeEmbed
            {...embedProps}
            videoId={videoId}
            options={{
              autoplay: embedProps.autoplay ? 1 : 0,
              mute: embedProps.muted ? 1 : 0,
              loop: embedProps.loop ? 1 : 0,
              controls: embedProps.controls !== false ? 1 : 0,
              modestbranding: 1,
              rel: 0,
            }}
          />
        </div>
      </div>
    );
  }

  if (platform === PLATFORMS.INSTAGRAM) {
    return (
      <div className={classes.embedContainer}>
        <div className={classes.verticalVideoWrapper}>
          <iframe
            src={finalEmbedUrl}
            scrolling="no"
            allowTransparency="true"
            allow="encrypted-media"
            title={embedProps.title || 'Instagram content'}
          />
        </div>
      </div>
    );
  }

  if (platform === PLATFORMS.TIKTOK) {
    return (
      <div className={classes.embedContainer}>
        <div className={classes.verticalVideoWrapper}>
          <iframe
            src={finalEmbedUrl}
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            title={embedProps.title || 'TikTok content'}
          />
        </div>
      </div>
    );
  }

  if (platform === PLATFORMS.FACEBOOK) {
    const isVertical = isFacebookVertical(finalEmbedUrl);

    return (
      <div className={classes.embedContainer}>
        <div
          className={
            isVertical ? classes.verticalVideoWrapper : classes.facebookWrapper
          }
        >
          <iframe
            src={finalEmbedUrl}
            scrolling="no"
            allowTransparency="true"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            title={embedProps.title || 'Facebook content'}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.embedContainer}>
      <div className={classes.youtubeWrapper}>
        <iframe
          src={finalEmbedUrl}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={embedProps.title || 'Embedded content'}
          style={{border: 'none'}}
        />
      </div>
    </div>
  );
}
